import React from 'react'
import { EmptyItem } from '../EmptyItem/EmptyItem'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { PaginationRange } from '@/components/shared/PaginationRange/PaginationRange'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface TableListCustomProps {
  children: React.ReactNode
  currentPage: number
  setCurrentPage: (page: number) => void
  emptyIconMessage?: React.ReactNode
  emptyDescriptionMessage?: string
  emptyTitleMessage?: string
  emptyFallback?: React.ReactNode
  loadingFallback?: React.ReactNode
  headerContent?: React.ReactNode
  variant?: 'table' | 'custom'
  headers?: { key: string; title: string | React.ReactNode }[]
  data: {
    data: any[]
    meta?: { last_page: number }
    links?: { next?: string | null; prev?: string | null }
  }
  isLoadingData: boolean
  datacy?: string
}

export default function TableListCustom({
  children,
  currentPage,
  setCurrentPage,
  emptyIconMessage,
  emptyDescriptionMessage,
  emptyTitleMessage,
  emptyFallback,
  loadingFallback,
  headerContent,
  variant = 'table',
  headers,
  data,
  datacy = 'table-list-custom',
  isLoadingData,
}: TableListCustomProps) {
  const totalPages = data?.meta?.last_page || 1

  return (
    <div>
      {headerContent}
      {data?.data?.length === 0 && !isLoadingData && (
        <>
          {emptyFallback ??
            (emptyTitleMessage &&
              emptyDescriptionMessage &&
              emptyIconMessage && (
                <EmptyItem
                  title={emptyTitleMessage}
                  description={emptyDescriptionMessage}
                  icon={emptyIconMessage}
                />
              ))}
        </>
      )}
      {isLoadingData ? (
        <>{loadingFallback ?? <TableSkeleton />}</>
      ) : (
        data?.data?.length > 0 && (
          <>
            {variant === 'table' ? (
              <div className="overflow-x-auto w-full">
                <Table className="min-w-full" data-cy={datacy}>
                  <TableHeader className="bg-[#18181b] text-xs font-semibold text-muted uppercase !h-[40px]">
                    <TableRow className="!h-[40px] hover:bg-transparent">
                      {(headers ?? []).map((header) => (
                        <TableCell key={header.key}>{header.title}</TableCell>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>{children}</TableBody>
                </Table>
              </div>
            ) : (
              <div data-cy={datacy}>{children}</div>
            )}
          </>
        )
      )}

      {totalPages > 1 && (
        <section className="p-4 w-full border-t border-t-colorBorder">
          <PaginationRange
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </section>
      )}
    </div>
  )
}
